import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Chip, TextField, InputAdornment, CircularProgress, Paper, useTheme, useMediaQuery,
} from '@mui/material';
import { Search, DeleteSweep } from '@mui/icons-material';
import apiClient from 'utils/ApiClient';
import { consistentSpinnerSx } from '../Logs/types';
import { buildSelectorString } from '../Logs/utils';

/**
 * Loki's labels / label-values / series APIs require a time window (omitting
 * start/end defaults to the last 6 hours, not "all time"). Settings preview
 * uses the last 30 days so Step 2 can list values within Loki's typical
 * max_query_length (~721h).
 */
const SETTINGS_LOKI_PREVIEW_RANGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface LokiConnectionFormSlice {
  moduleType: string;
  url: string;
  authMethod: string;
  basicAuthUser: string;
  basicAuthPassword: string;
  tlsSelfSignedCert: boolean;
  tlsClientAuth: boolean;
  tlsSkipValidation: boolean;
  httpHeaders: { key: string; value: string }[];
  requestTimeout: number;
}

interface LokiSettingsLabelBrowserProps {
  /** Remount / refetch when this changes (e.g. verified connection fingerprint + generation). */
  instanceKey: string;
  getConnectionBody: () => LokiConnectionFormSlice;
  selections: Record<string, string[]>;
  onSelectionsChange: (next: Record<string, string[]>) => void;
}

const previewBody = (conn: LokiConnectionFormSlice, extra: Record<string, unknown>) => ({
  moduleType: 'Loki',
  url: conn.url,
  authMethod: conn.authMethod,
  basicAuthUser: conn.basicAuthUser,
  basicAuthPassword: conn.basicAuthPassword,
  tlsSelfSignedCert: conn.tlsSelfSignedCert,
  tlsClientAuth: conn.tlsClientAuth,
  tlsSkipValidation: conn.tlsSkipValidation,
  httpHeaders: conn.httpHeaders,
  requestTimeout: conn.requestTimeout,
  ...extra,
});

export const LokiSettingsLabelBrowser: React.FC<LokiSettingsLabelBrowserProps> = ({
  instanceKey,
  getConnectionBody,
  selections,
  onSelectionsChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [allLabels, setAllLabels] = useState<string[]>([]);
  const [activeLabels, setActiveLabels] = useState<string[]>([]);
  const [labelValuesMap, setLabelValuesMap] = useState<Record<string, string[]>>({});
  const [originalLabelValues, setOriginalLabelValues] = useState<Record<string, string[]>>({});
  const [dialogSelectedValues, setDialogSelectedValues] = useState<Record<string, string[]>>({});
  const [disabledLabels, setDisabledLabels] = useState<string[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [valueFilter, setValueFilter] = useState('');

  const getPreviewValueSeriesRangeMs = useCallback(() => {
    const end = Date.now();
    return { start: end - SETTINGS_LOKI_PREVIEW_RANGE_MS, end };
  }, []);

  const selectionsRef = useRef(selections);
  selectionsRef.current = selections;

  const fetchSeriesAndUpdate = useCallback(
    async (currentSelections: Record<string, string[]>, modifiedLabel: string) => {
      const conn = getConnectionBody();
      const hasSelection = Object.values(currentSelections).some((v) => v.length > 0);

      if (!hasSelection) {
        setLabelValuesMap({ ...originalLabelValues });
        setDisabledLabels([]);
        return;
      }

      try {
        setSeriesLoading(true);
        const { start, end } = getPreviewValueSeriesRangeMs();
        const response = await apiClient.post('/il/settings/loki-preview/series', previewBody(conn, {
          selectedLabels: currentSelections,
          start,
          end,
        }));
        const seriesMap: Record<string, string[]> = response.data || {};

        setLabelValuesMap((prev) => {
          const updated = { ...prev };
          allLabels.forEach((label) => {
            if (label === modifiedLabel) return;
            updated[label] = seriesMap[label] ? seriesMap[label] : [];
          });
          return updated;
        });

        const newDisabled = allLabels.filter((l) => !seriesMap[l] || seriesMap[l].length === 0);
        setDisabledLabels(newDisabled);
      } catch {
        // keep current values
      } finally {
        setSeriesLoading(false);
      }
    },
    [getConnectionBody, getPreviewValueSeriesRangeMs, allLabels, originalLabelValues],
  );

  useEffect(() => {
    let cancelled = false;
    const savedSelections = { ...selectionsRef.current };

    const run = async () => {
      const conn = getConnectionBody();
      if (!conn.url?.trim()) return;

      setAllLabels([]);
      setActiveLabels([]);
      setLabelValuesMap({});
      setOriginalLabelValues({});
      setDialogSelectedValues({});
      setDisabledLabels([]);
      setValueFilter('');

      try {
        setLabelsLoading(true);
        const { start, end } = getPreviewValueSeriesRangeMs();
        const response = await apiClient.post(
          '/il/settings/loki-preview/labels',
          previewBody(conn, { start, end }),
        );
        if (cancelled) return;

        const raw: string[] = (response.data || []).filter((l: string) => typeof l === 'string' && l !== '__name__');
        const fetched: string[] = [...new Set(raw)].sort((a, b) => a.localeCompare(b));
        setAllLabels(fetched);
        setDisabledLabels([]);
        const valuesPromises = fetched.map(async (label) => {
          try {
            const valRes = await apiClient.post(
              '/il/settings/loki-preview/label-values',
              previewBody(conn, {
                label,
                selectedLabels: {},
                start,
                end,
              }),
            );
            return { label, values: (valRes.data || []) as string[] };
          } catch {
            return { label, values: [] as string[] };
          }
        });

        const results = await Promise.all(valuesPromises);
        if (cancelled) return;

        const newMap: Record<string, string[]> = {};
        results.forEach(({ label, values }) => {
          newMap[label] = values;
        });
        setLabelValuesMap(newMap);
        setOriginalLabelValues(newMap);

        // A saved key (even one with an empty array) means the user activated that
        // label in Step 1 — keep it active so the per-label column re-renders in
        // Step 2 and lets the user pick (or skip) values without losing scope.
        const allSavedKeys = Object.keys(savedSelections);
        const initialActive = allSavedKeys.filter((k) => fetched.includes(k));
        setActiveLabels(initialActive);
        setDialogSelectedValues({ ...savedSelections });
      } catch {
        if (!cancelled) {
          setAllLabels([]);
          setLabelValuesMap({});
          setOriginalLabelValues({});
          setActiveLabels([]);
          setDialogSelectedValues({});
        }
      } finally {
        if (!cancelled) setLabelsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [instanceKey, getConnectionBody, getPreviewValueSeriesRangeMs]);

  const handleLabelPillToggle = (label: string) => {
    if (activeLabels.includes(label)) {
      setActiveLabels((prev) => prev.filter((l) => l !== label));
      const next = { ...dialogSelectedValues };
      delete next[label];
      setDialogSelectedValues(next);
      onSelectionsChange(next);
      fetchSeriesAndUpdate(next, label);
    } else {
      setActiveLabels((prev) => [...prev, label]);
      // Persist the label even before any value is chosen, so the Logs page
      // can pre-select this label in Step 1 and offer all available values.
      const next: Record<string, string[]> = {
        ...dialogSelectedValues,
        [label]: dialogSelectedValues[label] || [],
      };
      setDialogSelectedValues(next);
      onSelectionsChange(next);
    }
  };

  const handleValueClick = (label: string, value: string) => {
    const current = dialogSelectedValues[label] || [];
    let newValues: string[];
    if (current.includes(value)) {
      newValues = current.filter((v) => v !== value);
    } else {
      newValues = [...current, value];
    }

    // Always keep the label key in the map (with an empty array if the user
    // cleared its values) — clicking values implies the label is active in
    // Step 1, so the scope must round-trip to MongoDB.
    const newSelections: Record<string, string[]> = { ...dialogSelectedValues, [label]: newValues };
    setDialogSelectedValues(newSelections);
    onSelectionsChange(newSelections);
    fetchSeriesAndUpdate(newSelections, label);
  };

  const handleClear = () => {
    const empty: Record<string, string[]> = {};
    setDialogSelectedValues(empty);
    onSelectionsChange(empty);
    setDisabledLabels([]);
    setLabelValuesMap({ ...originalLabelValues });
    setActiveLabels([]);
  };

  // A label persisted as an empty array still counts as a Step-1 selection,
  // so Clear should be enabled even when no Step-2 values are picked yet.
  const dialogHasAnySelection = Object.keys(dialogSelectedValues).length > 0;

  if (labelsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress size={32} variant="determinate" value={30} sx={consistentSpinnerSx} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading labels from Loki server...
        </Typography>
      </Box>
    );
  }

  if (allLabels.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No labels found in the last 30 days. The Loki server may have no data in that window.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          1. Select labels to search in
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Label names and values come from data Loki has seen in the last 30 days. Click a label to show its values.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: 0.75,
          }}
        >
          {allLabels.map((label) => {
            const isActive = activeLabels.includes(label);
            const isDisabled = disabledLabels.includes(label);
            const count = (labelValuesMap[label] || []).length;
            const displayLabel = count > 0 ? `${label} (${count})` : label;

            return (
              <Chip
                key={label}
                label={displayLabel}
                size={isMobile ? 'medium' : 'small'}
                clickable={!isDisabled}
                onClick={() => !isDisabled && handleLabelPillToggle(label)}
                color={isActive && !isDisabled ? 'secondary' : 'default'}
                variant={isActive && !isDisabled ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.85rem' : '0.78rem',
                  opacity: isDisabled ? 0.45 : 1,
                  cursor: isDisabled ? 'default' : 'pointer',
                  justifyContent: isMobile ? 'flex-start' : 'center',
                }}
              />
            );
          })}
        </Box>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              2. Find values for the selected labels
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Optional. Leave unselected to save the Step 1 labels only — Logs will show all live values for those labels.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<DeleteSweep />}
            onClick={handleClear}
            disabled={!dialogHasAnySelection}
            sx={{ textTransform: 'none' }}
          >
            Clear
          </Button>
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder="Filter expression for values"
          value={valueFilter}
          onChange={(e) => setValueFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />

        {seriesLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={16} variant="determinate" value={30} sx={consistentSpinnerSx} />
            <Typography variant="caption" color="text.secondary">Updating labels...</Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 1,
            overflowX: isMobile ? 'hidden' : 'auto',
            pb: 1,
            minHeight: isMobile ? 'auto' : 200,
          }}
        >
          {activeLabels
            .filter((label) => !disabledLabels.includes(label))
            .map((label) => {
              const values = labelValuesMap[label] || [];
              const selected = dialogSelectedValues[label] || [];
              const count = values.length;
              const filtered = valueFilter
                ? values.filter((v) => v.toLowerCase().includes(valueFilter.toLowerCase()))
                : values;

              return (
                <Box
                  key={label}
                  sx={{
                    minWidth: isMobile ? 'auto' : 160,
                    maxWidth: isMobile ? '100%' : 220,
                    flex: isMobile ? 'none' : '0 0 auto',
                    width: isMobile ? '100%' : 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'secondary.main',
                      color: (t) => t.palette.secondary.contrastText,
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} noWrap>
                      {label} ({count})
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, maxHeight: 240, overflowY: 'auto' }}>
                    {filtered.map((value) => {
                      const isSelected = selected.includes(value);
                      return (
                        <Box
                          key={value}
                          onClick={() => handleValueClick(label, value)}
                          sx={{
                            px: 1,
                            py: 0.4,
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            backgroundColor: isSelected ? 'secondary.main' : 'transparent',
                            color: (t) =>
                              isSelected ? t.palette.secondary.contrastText : t.palette.text.primary,
                            '&:hover': {
                              backgroundColor: isSelected ? 'secondary.dark' : 'action.hover',
                            },
                          }}
                        >
                          {value}
                        </Box>
                      );
                    })}
                    {filtered.length === 0 && (
                      <Typography variant="caption" color="text.disabled" sx={{ px: 1, py: 1, display: 'block' }}>
                        {valueFilter ? 'No matches' : 'No values'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}

          {activeLabels.filter((l) => !disabledLabels.includes(l)).length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Select labels above to see their values
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          3. Resulting selector
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            px: 1.5,
            py: 1,
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            wordBreak: 'break-all',
            backgroundColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
          }}
        >
          {buildSelectorString(dialogSelectedValues, null)}
        </Paper>
      </Box>
    </Box>
  );
};
